// Hook: warns (never blocks) before a write on a filière attached to none of the
// user's campuses. Returns a `guard(campusIds, action)` and the modal to render.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CuteConfirmModal from '@/shared/components/CuteConfirmModal';
import { getAvailableCampuses } from '@/services/groupsService';

interface CampusScope {
  loaded: boolean;
  hasMultipleCampuses: boolean;
  userHasGlobalAccess: boolean;
  userCampusIds: string[];
}

const INITIAL_SCOPE: CampusScope = {
  loaded: false,
  hasMultipleCampuses: false,
  userHasGlobalAccess: true,
  userCampusIds: [],
};

export interface CampusScopeWarning {
  /** True when the filière (given its campus links) is outside the user's campuses */
  isOutOfScope: (filiereCampusIds: string[] | undefined) => boolean;
  /** Runs `action` directly, or after the user confirms the warning */
  guard: (filiereCampusIds: string[] | undefined, action: () => void) => void;
  /** Render this once in the page */
  modal: React.ReactNode;
}

export function useCampusScopeWarning(): CampusScopeWarning {
  const [scope, setScope] = useState<CampusScope>(INITIAL_SCOPE);
  const [isOpen, setIsOpen] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAvailableCampuses()
      .then((ctx) => {
        if (cancelled) return;
        setScope({
          loaded: true,
          hasMultipleCampuses: ctx.hasMultipleCampuses,
          userHasGlobalAccess: ctx.userHasGlobalAccess,
          userCampusIds: ctx.campuses.map((c) => c.id),
        });
      })
      .catch((err: unknown) => {
        // The warning is advisory: without scope information we simply do not warn
        console.error('Impossible de charger le périmètre campus:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isOutOfScope = useCallback(
    (filiereCampusIds: string[] | undefined): boolean => {
      if (!scope.loaded || !scope.hasMultipleCampuses || scope.userHasGlobalAccess) return false;
      if (!filiereCampusIds || filiereCampusIds.length === 0) return false;
      return !filiereCampusIds.some((id) => scope.userCampusIds.includes(id));
    },
    [scope],
  );

  const guard = useCallback(
    (filiereCampusIds: string[] | undefined, action: () => void): void => {
      if (!isOutOfScope(filiereCampusIds)) {
        action();
        return;
      }
      pendingAction.current = action;
      setIsOpen(true);
    },
    [isOutOfScope],
  );

  const close = (): void => {
    pendingAction.current = null;
    setIsOpen(false);
  };

  const confirm = (): void => {
    const action = pendingAction.current;
    pendingAction.current = null;
    setIsOpen(false);
    action?.();
  };

  const modal = (
    <CuteConfirmModal
      isOpen={isOpen}
      title="Filière d'un autre campus"
      message="Cette filière n'est rattachée à aucun de vos campus. Voulez-vous vraiment continuer ?"
      confirmLabel="Continuer"
      cancelLabel="Annuler"
      variant="warning"
      onConfirm={confirm}
      onCancel={close}
    />
  );

  return { isOutOfScope, guard, modal };
}
